import { useEffect, useMemo, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Download,
  Scale,
  Search,
} from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  PageHeaderSkeleton,
  StatCardsSkeleton,
  TableSkeleton,
} from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { TablePagination } from "~/components/ui/table-pagination";
import { useCostingPeriods } from "~/hooks/costing";
import { usePagination } from "~/hooks/usePagination";
import { useToast } from "~/hooks/use-toast";
import {
  exportInventoryVarianceToExcel,
  inventoryVarianceService,
} from "~/services/costing";
import { formatCurrency } from "~/utils/helpers";

import { PeriodSelector } from "./components/CosteoShared";

const COUNT_DATE = "2026-06-30";
const EPS = 1; // CLP tolerance for "con diferencia"

const qty = (n: number | null | undefined) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("es-CL", { maximumFractionDigits: 2 }).format(n);

const clp = (n: number | null | undefined) =>
  n == null ? "—" : `$${formatCurrency(n)}`;

const amountClass = (n: number | null | undefined) =>
  n == null || Math.abs(n) < EPS
    ? "text-muted-foreground"
    : n > 0
      ? "text-green-700"
      : "text-red-700";

type SortKey =
  | "bucket"
  | "code"
  | "name"
  | "real_qty"
  | "real_unit_cost"
  | "real_valorizado"
  | "teorico_qty"
  | "teorico_unit_cost"
  | "teorico_valorizado"
  | "dif_cantidad"
  | "dif_costo";

const NUMERIC_KEYS = new Set<SortKey>([
  "real_qty",
  "real_unit_cost",
  "real_valorizado",
  "teorico_qty",
  "teorico_unit_cost",
  "teorico_valorizado",
  "dif_cantidad",
  "dif_costo",
]);

type SortState = { key: SortKey; dir: "asc" | "desc" };

interface SortHeaderProps {
  label: string;
  sortKey: SortKey;
  sort: SortState;
  onSort: (key: SortKey) => void;
  align?: "left" | "right";
  className?: string;
}

function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
  align = "left",
  className,
}: SortHeaderProps) {
  const active = sort.key === sortKey;
  const Icon = active ? (sort.dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <TableHead
      onClick={() => onSort(sortKey)}
      className={`cursor-pointer select-none whitespace-nowrap hover:bg-muted/50 ${
        align === "right" ? "text-right" : ""
      } ${className ?? ""}`}
    >
      <span
        className={`inline-flex items-center gap-1 ${
          align === "right" ? "flex-row-reverse" : ""
        }`}
      >
        {label}
        <Icon
          className={`h-3.5 w-3.5 transition-transform duration-200 ${
            active ? "text-foreground" : "text-muted-foreground/40"
          }`}
        />
      </span>
    </TableHead>
  );
}

export function InventoryVariancePage() {
  const { toast } = useToast();
  const { data: periods = [] } = useCostingPeriods();
  const [period, setPeriod] = useState<string>("");
  // Default to the latest CLOSED month (consistent with Movimiento MP/PT + Inventario Final).
  useEffect(() => {
    if (!period && periods.length > 0) {
      const closed = periods.find((p) => p.status === "closed");
      setPeriod((closed ?? periods[0]).period);
    }
  }, [periods, period]);
  // The count is keyed by the calendar month-end date (e.g. 2026-07 → 2026-07-31).
  const countDate = period
    ? dayjs(`${period}-01`).endOf("month").format("YYYY-MM-DD")
    : COUNT_DATE;
  const [search, setSearch] = useState("");
  const [bucket, setBucket] = useState<"all" | "MP" | "PT">("all");
  const [estado, setEstado] = useState<
    "all" | "diff" | "sin_registro" | "con_registro"
  >("all");
  const [sort, setSort] = useState<SortState>({
    key: "real_valorizado",
    dir: "desc",
  });

  const handleSort = (key: SortKey) =>
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: NUMERIC_KEYS.has(key) ? "desc" : "asc" }
    );

  const { data, isLoading } = useQuery({
    queryKey: ["inventory-variance", countDate],
    queryFn: () => inventoryVarianceService.getVariance(countDate),
  });

  const rows = useMemo(() => data ?? [], [data]);

  const kpis = useMemo(() => {
    let real = 0,
      mp = 0,
      pt = 0,
      sistema = 0,
      difSystem = 0,
      sinRegCount = 0,
      sinRegVal = 0,
      conDif = 0;
    for (const r of rows) {
      real += r.real_valorizado ?? 0;
      if (r.bucket === "MP") mp += r.real_valorizado ?? 0;
      else pt += r.real_valorizado ?? 0;
      if (r.in_system) {
        sistema += r.teorico_valorizado ?? 0;
        difSystem += r.diferencia_valorizada ?? 0;
        if (Math.abs(r.diferencia_valorizada ?? 0) >= EPS) conDif++;
      } else {
        sinRegCount++;
        sinRegVal += r.real_valorizado ?? 0;
      }
    }
    return { real, mp, pt, sistema, difSystem, sinRegCount, sinRegVal, conDif };
  }, [rows]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (bucket !== "all" && r.bucket !== bucket) return false;
      if (estado === "sin_registro" && r.in_system) return false;
      if (estado === "con_registro" && !r.in_system) return false;
      if (
        estado === "diff" &&
        !(r.in_system && Math.abs(r.diferencia_valorizada ?? 0) >= EPS)
      )
        return false;
      if (
        term &&
        !r.code.toLowerCase().includes(term) &&
        !r.name.toLowerCase().includes(term)
      )
        return false;
      return true;
    });
  }, [rows, search, bucket, estado]);

  const sorted = useMemo(() => {
    const val = (r: (typeof filtered)[number]): number | string | null => {
      switch (sort.key) {
        case "bucket":
          return r.bucket ?? "";
        case "code":
          return r.code ?? "";
        case "name":
          return r.name ?? "";
        case "real_qty":
          return r.real_qty ?? null;
        case "real_unit_cost":
          return r.real_unit_cost ?? null;
        case "real_valorizado":
          return r.real_valorizado ?? null;
        case "teorico_qty":
          return r.in_system ? (r.teorico_qty ?? null) : null;
        case "teorico_unit_cost":
          return r.in_system ? (r.teorico_unit_cost ?? null) : null;
        case "teorico_valorizado":
          return r.in_system ? (r.teorico_valorizado ?? null) : null;
        case "dif_cantidad":
          return r.in_system ? (r.real_qty ?? 0) - (r.teorico_qty ?? 0) : null;
        case "dif_costo":
          return r.in_system ? (r.diferencia_valorizada ?? null) : null;
      }
    };
    const mult = sort.dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const va = val(a);
      const vb = val(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1; // nulls always last
      if (vb == null) return -1;
      if (typeof va === "string" || typeof vb === "string") {
        return String(va).localeCompare(String(vb), "es") * mult;
      }
      return (va - vb) * mult;
    });
  }, [filtered, sort]);

  const { page, setPage, pageCount, pageItems, total } = usePagination(
    sorted,
    20
  );

  const handleExport = () => {
    if (!rows.length) return;
    try {
      exportInventoryVarianceToExcel(rows, countDate);
    } catch {
      toast({
        title: "Error al exportar",
        description: "No se pudo generar el archivo Excel.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <PageHeaderSkeleton withAction />
        <StatCardsSkeleton count={4} />
        <TableSkeleton rows={12} columns={8} />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Variación Real vs Teórico"
        description="Inventario final real (Softland, consolidado) vs. el stock del sistema, valorizado. El final del mes es el inicial del siguiente. Solo lectura — no modifica stock."
        actions={
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            <PeriodSelector periods={periods} value={period} onChange={setPeriod} />
            <Button
              variant="outline"
              className="h-9"
              onClick={handleExport}
              disabled={!rows.length}
            >
              <Download className="mr-2 h-4 w-4" />
              Exportar
            </Button>
          </div>
        }
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Valorizado real
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">${formatCurrency(kpis.real)}</p>
            <p className="text-xs text-muted-foreground">
              MP ${formatCurrency(kpis.mp)} · PT ${formatCurrency(kpis.pt)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Valorizado sistema
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">${formatCurrency(kpis.sistema)}</p>
            <p className="text-xs text-muted-foreground">Ítems con registro</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Diferencia (real − sistema)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className={`text-2xl font-bold ${amountClass(kpis.difSystem)}`}>
              ${formatCurrency(kpis.difSystem)}
            </p>
            <p className="text-xs text-muted-foreground">
              {kpis.conDif} con diferencia
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Sin registro en sistema
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{kpis.sinRegCount}</p>
            <p className="text-xs text-muted-foreground">
              ${formatCurrency(kpis.sinRegVal)} real
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="relative sm:w-72">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar por código o nombre…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 pl-9"
          />
        </div>
        <Select value={bucket} onValueChange={(v) => setBucket(v as typeof bucket)}>
          <SelectTrigger className="h-9 w-full sm:w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">MP y PT</SelectItem>
            <SelectItem value="MP">Materia Prima (MP)</SelectItem>
            <SelectItem value="PT">Producto Terminado (PT)</SelectItem>
          </SelectContent>
        </Select>
        <Select value={estado} onValueChange={(v) => setEstado(v as typeof estado)}>
          <SelectTrigger className="h-9 w-full sm:w-52">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="diff">Con diferencia</SelectItem>
            <SelectItem value="con_registro">Con registro en sistema</SelectItem>
            <SelectItem value="sin_registro">Sin registro en sistema</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortHeader label="Bucket" sortKey="bucket" sort={sort} onSort={handleSort} />
                  <SortHeader label="Código" sortKey="code" sort={sort} onSort={handleSort} />
                  <SortHeader label="Nombre" sortKey="name" sort={sort} onSort={handleSort} />
                  <SortHeader label="Cant. real" sortKey="real_qty" sort={sort} onSort={handleSort} align="right" />
                  <SortHeader label="Costo unit." sortKey="real_unit_cost" sort={sort} onSort={handleSort} align="right" />
                  <SortHeader label="Valorizado real" sortKey="real_valorizado" sort={sort} onSort={handleSort} align="right" />
                  <SortHeader label="Cant. sistema" sortKey="teorico_qty" sort={sort} onSort={handleSort} align="right" className="border-l" />
                  <SortHeader label="Costo unit. sist." sortKey="teorico_unit_cost" sort={sort} onSort={handleSort} align="right" />
                  <SortHeader label="Valorizado sistema" sortKey="teorico_valorizado" sort={sort} onSort={handleSort} align="right" />
                  <SortHeader label="Dif. cantidad" sortKey="dif_cantidad" sort={sort} onSort={handleSort} align="right" className="border-l" />
                  <SortHeader label="Dif. costo" sortKey="dif_costo" sort={sort} onSort={handleSort} align="right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="h-40 p-0">
                      <EmptyState
                        icon={Scale}
                        title="Sin resultados"
                        description="Ajusta los filtros o la fecha."
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  pageItems.map((r, i) => (
                    <TableRow key={`${r.code}-${i}`}>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {r.bucket}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {r.code}
                      </TableCell>
                      <TableCell className="max-w-[300px] truncate font-medium">
                        {r.name}
                        {!r.in_system && (
                          <Badge
                            variant="outline"
                            className="ml-2 text-[10px] text-muted-foreground"
                          >
                            sin registro
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {qty(r.real_qty)}{" "}
                        <span className="text-xs text-muted-foreground">
                          {r.base_unit}
                        </span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {clp(r.real_unit_cost)}
                      </TableCell>
                      <TableCell className="text-right font-medium tabular-nums">
                        {clp(r.real_valorizado)}
                      </TableCell>
                      <TableCell className="border-l text-right tabular-nums text-muted-foreground">
                        {r.in_system ? (
                          <>
                            {qty(r.teorico_qty)}{" "}
                            <span className="text-xs">{r.base_unit}</span>
                          </>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {r.in_system ? clp(r.teorico_unit_cost) : "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {r.in_system ? clp(r.teorico_valorizado) : "—"}
                      </TableCell>
                      <TableCell
                        className={`border-l text-right font-medium tabular-nums ${r.in_system ? amountClass((r.real_qty ?? 0) - (r.teorico_qty ?? 0)) : "text-muted-foreground"}`}
                      >
                        {r.in_system
                          ? qty((r.real_qty ?? 0) - (r.teorico_qty ?? 0))
                          : "—"}
                      </TableCell>
                      <TableCell
                        className={`text-right font-semibold tabular-nums ${r.in_system ? amountClass(r.diferencia_valorizada) : "text-muted-foreground"}`}
                      >
                        {r.in_system ? clp(r.diferencia_valorizada) : "—"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {filtered.length > 0 && (
            <div className="border-t p-4">
              <TablePagination
                page={page}
                pageCount={pageCount}
                totalItems={total}
                pageSize={20}
                onPageChange={setPage}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        Real = inventario final Softland consolidado (cantidad × costo del archivo;
        aceites en KG). Sistema = stock del sistema al cierre de {countDate}:
        ingredientes reconstruidos (stock actual − movimientos posteriores) y productos
        terminados según el cierre de costeo del mes (el inventario final es el inicial
        del mes siguiente). Diferencia = valorizado real − sistema. Los ítems sin registro
        en el sistema a esa fecha muestran solo el real. Esta vista no modifica datos.
      </p>
    </PageContainer>
  );
}
